// IKEA door configurator. Wrapped in an IIFE to avoid global collisions with product-builder-2.js.
(function () {
const PROP_EDGES = "Edges";
const PROP_HANDLES = "Handles";
const PROP_HANDLE_SETUP = "Handle Setup";
// Technical linkage keys are underscore-prefixed so Shopify's checkout and
// order emails don't print them. The one visible add-on property is PROP_FOR:
// "Door 2 – IKEA Kitchen Door 60 × 40". Lines added before this rename still
// carry the old visible keys, so readers accept both (see linkedKeyOf()).
const PROP_LINKED_KEY = "_LinkedTo";
const LEGACY_PROP_LINKED_KEY = "Linked To";
const PROP_PARENT_TYPE = "_ParentProduct";
const LEGACY_PROP_PARENT_TYPE = "Parent Product";
const PROP_NUMBER = "_Number";
const LEGACY_PROP_NUMBER = "Number";
const PROP_FOR = "For";
const PROP_BUILDER = "_Builder";
const PROP_DOOR_SEQ = "_DoorSeq";
const PROP_DOOR_REF = "_DoorRef";
// Visible door number assigned once at add time (see product-builder-2.js
// nextDoorNumber()); stored on the door line and copied onto its add-ons.
const PROP_DOOR = "Door";
// Hidden ordered quantity for lines priced by unit — the line quantity
// itself is the unit count, not what the shopper ordered.
const PROP_QTY = "_Qty";
const IKEA_DEBUG = false;

// Which configurator this page is (e.g. "kitchen-builder" / "wardrobe-builder").
// Doors are tagged with this on add, so each configurator only renders its own
// doors in the edge/handle steps — preventing kitchen↔wardrobe cross-contamination.
function getBuilderType() {
  const el = document.querySelector("[data-builder-type]");
  return (el && el.dataset.builderType) || "";
}

const EDGE_STRINGS = (() => {
  try {
    const el = document.getElementById("builder-edge-strings");
    return el ? JSON.parse(el.textContent) : {};
  } catch {
    return {};
  }
})();

const HANDLE_STRINGS = (() => {
  const fallback = {
    no_handle: "No handle for this door",
    clear: "Clear handle selection",
    choose_label: "Choose handle type & size",
    select_placeholder: "Select a handle",
  };
  try {
    const el = document.getElementById("builder-handle-strings");
    return el ? { ...fallback, ...JSON.parse(el.textContent) } : fallback;
  } catch {
    return fallback;
  }
})();

const NO_HANDLE_VALUE = "__no_handle__";

// ========= "NO HANDLE FOR THIS DOOR" =========
// An explicit per-door choice, so a door left without a handle reads as a
// decision rather than an unfinished step. The cart only ever records handles
// that were actually chosen, so the opt-out is kept in localStorage against the
// door's line-item key (scoped per builder) and restored on every render.
const NO_HANDLE_STORAGE_KEY = "brandler:ikea:no-handle";

function readNoHandleStore() {
  try {
    const raw = window.localStorage.getItem(NO_HANDLE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeNoHandleStore(store) {
  try {
    window.localStorage.setItem(NO_HANDLE_STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* Private mode / storage disabled — the choice just won't survive a reload. */
  }
}

function noHandleKeys() {
  const store = readNoHandleStore();
  const keys = store[getBuilderType() || "default"];
  return Array.isArray(keys) ? keys : [];
}

function isNoHandleDoor(doorKey) {
  return noHandleKeys().includes(doorKey);
}

function setNoHandleDoor(doorKey, enabled) {
  const store = readNoHandleStore();
  const bucket = getBuilderType() || "default";
  const keys = new Set(Array.isArray(store[bucket]) ? store[bucket] : []);
  if (enabled) {
    keys.add(doorKey);
  } else {
    keys.delete(doorKey);
  }
  store[bucket] = Array.from(keys);
  writeNoHandleStore(store);
}

// Line-item keys change as the cart changes; drop any that no longer exist so
// the store can't grow forever or resurrect on a recycled key.
function pruneNoHandleDoors(validKeys) {
  const store = readNoHandleStore();
  const bucket = getBuilderType() || "default";
  const keys = Array.isArray(store[bucket]) ? store[bucket] : [];
  const kept = keys.filter((key) => validKeys.includes(key));
  if (kept.length !== keys.length) {
    store[bucket] = kept;
    writeNoHandleStore(store);
  }
}

function log(...args) {
  if (IKEA_DEBUG) {
    console.log("[IKEA-SETUP]", ...args);
  }
}
const POSITION_CLASS_MAP = {
  "Top Right": "corner-tr",
  "Top Left": "corner-tl",
  "Bottom Left": "corner-bl",
  "Bottom Right": "corner-br",
  "Middle Right": "corner-mr",
  "Middle Left": "corner-ml",
  "Middle Middle": "corner-mm",
};

const HANDLES_CONFIG = [
  {
    title: "60mm Corner",
    price: "£20.00",
    productId: "15988371784025",
    variants: [
      {
        pos: "Top Right",
        variantId: "56894821400921",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/60mm-top-right-corner-door-handle-position-diagram-white.png",
      },
      {
        pos: "Top Left",
        variantId: "56896318570841",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/60mm-top-left-corner-door-handle-position-diagram-white.png",
      },
      {
        pos: "Bottom Left",
        variantId: "56896318603609",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/btm_left_60.png",
      },
      {
        pos: "Bottom Right",
        variantId: "56896318636377",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/60mm-bottom-right-corner-door-handle-position-diagram-white.png",
      },
      {
        pos: "Middle Right",
        variantId: "57024573636953",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/handle-middle-right.png",
      },
      {
        pos: "Middle Left",
        variantId: "57024573669721",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/handle-middle-left.png",
      },
      {
        pos: "Middle Middle",
        variantId: "57165556744537",
        img: "https://cdn.shopify.com/s/files/1/0881/0676/2585/files/top-middle-handle.png?v=1770027695"
      }
    ],
  },

  {
    title: "100mm Corner",
    productId: "16001419182425",
    price: "£22.00",
    variants: [
      {
        pos: "Top Right",
        variantId: "56896318701913",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/100mm-top-right-door-handle-position-diagram-white.png",
      },
      {
        pos: "Top Left",
        variantId: "56896318734681",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/100mm-top-left-corner-door-handle-position-diagram-white.png",
      },
      {
        pos: "Bottom Left",
        variantId: "56896318767449",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/100mm-bottom-left-door-handle-position-diagram-white.png",
      },
      {
        pos: "Bottom Right",
        variantId: "56896318800217",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/100mm-bottom-right-corner-door-handle-position-diagram-white.png",
      },
      {
        pos: "Middle Right",
        variantId: "57024574325081",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/handle-middle-right.png",
      },
      {
        pos: "Middle Left",
        variantId: "57024574292313",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/handle-middle-left.png",
      },
      {
        pos: "Middle Middle",
        variantId: "57167242723673",
        img: "https://cdn.shopify.com/s/files/1/0881/0676/2585/files/top-middle-handle.png?v=1770027695"
      }
    ],
  },

  {
    title: "120mm Inset",
    productId: "16001419608409",
    price: "£26.00",
    variants: [
      {
        pos: "Top Right",
        variantId: "56896318996825",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/top-right-door-120-door-handle-position-diagram-white.png",
      },
      {
        pos: "Top Left",
        variantId: "56896319029593",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/120mm-top-left-door-handle-position-diagram-white.png",
      },
      {
        pos: "Bottom Left",
        variantId: "56896319062361",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/bottom-left-door-120-door-handle-position-diagram-white.png",
      },
      {
        pos: "Bottom Right",
        variantId: "56896319095129",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/120mm-bottom-right-door-handle-position-diagram-white.png",
      },
      {
        pos: "Middle Right",
        variantId: "57024575963481",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/handle-middle-right.png",
      },
      {
        pos: "Middle Left",
        variantId: "57024575930713",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/handle-middle-left.png",
      },
      {
        pos: "Middle Middle",
        variantId: "57167244656985",
        img: "https://cdn.shopify.com/s/files/1/0881/0676/2585/files/top-middle-handle.png?v=1770027695"
      }
    ],
  },

  {
    title: "200mm Inset",
    productId: "16001419673945",
    price: "£30.00",
    variants: [
      {
        pos: "Top Right",
        variantId: "56896319357273",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/top-right-door-200-door-handle-position-diagram-white.png",
      },
      {
        pos: "Top Left",
        variantId: "56896319390041",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/top-left-door-200mm-door-handle-position-diagram-white.png",
      },
      {
        pos: "Bottom Left",
        variantId: "56896319422809",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/btm-left-200mm-door-handle-position-diagram-white.png",
      },
      {
        pos: "Bottom Right",
        variantId: "56896319455577",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/btm-right-door-200mm-door-handle-position-diagram-white.png",
      },
      {
        pos: "Middle Right",
        variantId: "57024578584921",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/handle-middle-right.png",
      },
      {
        pos: "Middle Left",
        variantId: "57024578552153",
        img: "//b0a180-f4.myshopify.com/cdn/shop/files/handle-middle-left.png",
      },
      {
        pos: "Middle Middle",
        variantId: "57167245607257",
        img: "https://cdn.shopify.com/s/files/1/0881/0676/2585/files/top-middle-handle.png?v=1770027695"
      }
    ],
  },
];

const INPUT_CART_OPTION_SETUP = `<input type="hidden" class="input-cart-option" name="door_option_added" value="no">
            <input type="hidden" class="input-cart-option" name="edge_type" value="">
            <input type="hidden" class="input-cart-option" name="edge_id" value="">
            <input type="hidden" class="input-cart-option" name="handle_id" value="">
            <input type="hidden" class="input-cart-option" name="edge_position" value="">`;

function normalize(str) {
  return String(str || "").trim();
}

function containsIkeaDoor(title) {
  const normalizedTitle = normalize(title).toUpperCase();

  const hasIkea = normalizedTitle.includes("IKEA");
  const hasPanel = normalizedTitle.includes("PANEL");

  const result = hasIkea && !hasPanel;

  log(
    "Check title:",
    title,
    "| hasIkea:", hasIkea,
    "| hasPanel:", hasPanel,
    "→", result
  );

  return result;
}

// Edges/handles are linked add-ons (their titles also contain "IKEA"), not doors.
const ADDON_MARKER_PROPS = [
  "Edge Type",
  "Edge Position",
  "Custom Edge Position",
  "Handle",
  "Handle Position",
  PROP_HANDLE_SETUP,
];

function isLinkedAddon(item) {
  const props = getItemProps(item);
  if (
    props._linked_type ||
    linkedKeyOf(item) ||
    props[PROP_PARENT_TYPE] ||
    props[LEGACY_PROP_PARENT_TYPE] ||
    props[PROP_DOOR_REF]
  ) {
    return true;
  }
  return ADDON_MARKER_PROPS.some((name) => normalize(props[name]));
}

// The cart line key of the door this add-on belongs to ("" for doors and for
// lines with no link). Reads the hidden key first, then the pre-rename one.
function linkedKeyOf(item) {
  const props = getItemProps(item);
  return props[PROP_LINKED_KEY] || props[LEGACY_PROP_LINKED_KEY] || "";
}

// The door's shared grouping number, from either key spelling.
function numberOf(item) {
  const props = getItemProps(item);
  return props[PROP_NUMBER] || props[LEGACY_PROP_NUMBER] || "";
}

// Visible "For" value for an add-on: "Door 2 – IKEA Kitchen Door 60 × 40".
// This is what the checkout shows instead of the raw cart line key.
function describeDoorFor(doorItem) {
  const props = getItemProps(doorItem);
  const title = doorItem?.title || "";
  return props[PROP_DOOR] ? `Door ${props[PROP_DOOR]} – ${title}` : title;
}

// Doors are numbered by the order they were added, not by their position in
// cart.items — Shopify prepends new lines, so that order runs backwards.
// Doors with no recorded sequence predate it, so they sort first, in reverse
// cart order.
function sortDoorsByAddOrder(items) {
  return items
    .map((item, cartIndex) => ({ item, cartIndex }))
    .sort((a, b) => {
      const aSeq = normalize(getItemProps(a.item)[PROP_DOOR_SEQ]);
      const bSeq = normalize(getItemProps(b.item)[PROP_DOOR_SEQ]);
      if (aSeq && bSeq) return Number(aSeq) - Number(bSeq);
      if (aSeq) return 1;
      if (bSeq) return -1;
      return b.cartIndex - a.cartIndex;
    })
    .map((entry) => entry.item);
}

function isIkeaDoorItem(item) {
  return containsIkeaDoor(item?.title) && !isLinkedAddon(item);
}

function getItemProps(item) {
  return item && typeof item.properties === "object" && item.properties
    ? item.properties
    : {};
}

function checkActiveProp(props, propName, expectedValue) {
  return normalize(props?.[propName]) === normalize(expectedValue);
}

// Individual ("Custom") edges that the shopper can toggle freely.
const CUSTOM_EDGE_VALUES = ["Top Side", "Left Side", "Right Side", "Bottom"];

// Custom edges are stored as a comma-separated list (e.g. "Top Side, Left Side"),
// so membership is checked against the parsed list rather than full equality.
function propHasEdge(props, propName, value) {
  const raw = normalize(props?.[propName]);
  if (!raw) return false;
  return raw
    .split(",")
    .map((part) => normalize(part))
    .includes(normalize(value));
}

async function fetchCart() {
  log("Fetching cart...");
  // no-store: a bfcache/disk-cached /cart.js is the difference between the
  // edge/handle step showing the door that was just added and showing nothing.
  const res = await fetch("/cart.js", {
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    console.error("[IKEA-SETUP] cart.js failed", res.status);
    throw new Error(`Failed to load cart.js: ${res.status}`);
  }

  const cart = await res.json();
  log("Cart loaded", cart);
  return cart;
}

// ========= UI BUILDERS =========

// EDGES
function buildEdgesBlock({
  title,
  ind,
  doorNumber,
  key,
  variantId,
  props,
  optionName = PROP_EDGES,
}) {
  const list = document.createElement("div");
  list.classList.add("list");
  list.setAttribute("data-item-key", key);
  list.setAttribute("data-variant-id", variantId);

  // Same number as the cart (counts every door already in it), not the
  // position in this builder's list.
  const doorLabel = doorNumber || ind + 1;
  list.innerHTML = `
    <h2><span class="door-number">Door ${doorLabel}</span> — ${title}</h2>
    <input type="hidden" name="item-key" value="${key}">
    <input type="hidden" name="propperties" value="">
    <div id="${optionName}" class="edges_setup" data-prop-name="${optionName}">
      <div data-value="All Edges" class="edge_type ${
        checkActiveProp(props, optionName, "All Edges") ? "active" : ""
      }" data-component="option">
        <div class="name">${EDGE_STRINGS.all_name}</div>
        <img src="https://cdn.shopify.com/s/files/1/0881/0676/2585/files/all.png?v=1747398367" alt="all">
        <div class="info">${EDGE_STRINGS.all_info}</div>
      </div>

      <div data-value="Floor Unit" class="edge_type ${
        checkActiveProp(props, optionName, "Floor Unit") ? "active" : ""
      }" data-component="option">
        <div class="name">${EDGE_STRINGS.floor_name}</div>
        <img src="https://cdn.shopify.com/s/files/1/0881/0676/2585/files/all_1.png?v=1749046044" alt="all">
        <div class="info">${EDGE_STRINGS.floor_info}</div>
      </div>

      <div data-value="Wall Unit" class="edge_type ${
        checkActiveProp(props, optionName, "Wall Unit") ? "active" : ""
      }" data-component="option">
        <div class="name">${EDGE_STRINGS.wall_name}</div>
        <img src="https://cdn.shopify.com/s/files/1/0881/0676/2585/files/all_2.png?v=1749046005" alt="all">
        <div class="info">${EDGE_STRINGS.wall_info}</div>
      </div>

      <div data-value="Tall Unit" class="edge_type ${
        checkActiveProp(props, optionName, "Tall Unit") ? "active" : ""
      }" data-component="option">
        <div class="name">${EDGE_STRINGS.tall_name}</div>
        <img src="https://cdn.shopify.com/s/files/1/0881/0676/2585/files/all_3.png?v=1749045983" alt="all">
        <div class="info">${EDGE_STRINGS.tall_info}</div>
      </div>

      <div data-value="No Edges" class="edge_type ${
        checkActiveProp(props, optionName, "No Edges") ? "active" : ""
      }" data-component="option">
        <div class="name">${EDGE_STRINGS.no_edges_name}</div>
        <img src="https://cdn.shopify.com/s/files/1/0881/0676/2585/files/all_4.png?v=1749045947" alt="all">
        <div class="info">${EDGE_STRINGS.no_edges_info}</div>
      </div>

      <div data-value="Custom" class="edge_type edge_type--custom ${
        CUSTOM_EDGE_VALUES.some((v) => propHasEdge(props, optionName, v))
          ? "active"
          : ""
      }">
        <div class="name">${EDGE_STRINGS.custom_name}</div>
        <div class="edge-total-price custom-edge-total">£0.00</div>
        <div class="custom-block">
          <div data-value="square" class="edge_type_option square" data-component="option-radio"></div>
          <div data-value="Top Side" class="edge_type_option circle top ${
            propHasEdge(props, optionName, "Top Side") ? "active" : ""
          }" data-component="option-radio"></div>
          <div data-value="Left Side" class="edge_type_option circle left ${
            propHasEdge(props, optionName, "Left Side") ? "active" : ""
          }" data-component="option-radio"></div>
          <div data-value="Right Side" class="edge_type_option circle right ${
            propHasEdge(props, optionName, "Right Side") ? "active" : ""
          }" data-component="option-radio"></div>
          <div data-value="Bottom" class="edge_type_option circle bottom ${
            propHasEdge(props, optionName, "Bottom") ? "active" : ""
          }" data-component="option-radio"></div>
        </div>
        <div class="info">${EDGE_STRINGS.custom_info}</div>
      </div>
    </div>
  `;

  return list;
}

// HANDLES
function buildHandlesBlock({ title, ind, doorNumber, key, variantId, props }) {
  const wrap = document.createElement("div");
  wrap.classList.add("list", "list--handles");
  wrap.setAttribute("data-item-key", key);
  wrap.setAttribute("data-variant-id", variantId);

  // Restore the shopper's saved handle (size + position) so re-opening the step
  // — or the panel rebuilding after another door is added — keeps the choice.
  const defaultHandleIndex = Number.isInteger(props._handleIndex)
    ? props._handleIndex
    : -1;
  const savedPosition = normalize(props[PROP_HANDLE_SETUP]);
  const noHandle = defaultHandleIndex < 0 && isNoHandleDoor(key);

  if (noHandle) wrap.classList.add("is-no-handle");
  if (!noHandle && defaultHandleIndex < 0) wrap.classList.add("is-no-selection");

  // Same number as the cart (counts every door already in it), not the
  // position in this builder's list.
  const doorLabel = doorNumber || ind + 1;
  wrap.innerHTML = `
    <h3><span class="door-number">Door ${doorLabel}</span> — ${title}</h3>

    <input type="hidden" name="item-key" value="${key}">
    <input type="hidden" name="propperties" value="${PROP_HANDLES}">

    <div class="door-handle-select">
      <select id="door-handle-select-${ind}" class="door-handle-select__select" data-id="${PROP_HANDLES}" data-component="select">
        <option value="" disabled ${
          !noHandle && defaultHandleIndex < 0 ? "selected" : ""
        }>${HANDLE_STRINGS.select_placeholder}</option>
        <option value="${NO_HANDLE_VALUE}" ${
          noHandle ? "selected" : ""
        }>${HANDLE_STRINGS.no_handle}</option>
        ${HANDLES_CONFIG.map(
          (h, index) => `
            <option value="${h.title}" data-product-id="${h.productId}" ${
            index === defaultHandleIndex ? "selected" : ""
          }>
              ${h.title} - ${h.price}
            </option>
          `
        ).join("")}
      </select>
    </div>

    <div class="door-handle-options" data-component="handle-options">

      ${HANDLES_CONFIG.map((h, index) => {
        const isDefaultGroup = index === defaultHandleIndex;
        // Only the restored size keeps the saved position highlighted; every
        // other size opens on its first diagram.
        const savedIdx =
          isDefaultGroup && savedPosition
            ? h.variants.findIndex((v) => normalize(v.pos) === savedPosition)
            : -1;
        const shownIdx = savedIdx > -1 ? savedIdx : 0;

        return `
        <div class="handles-setup ${isDefaultGroup ? "active" : ""}"
          style="display:${isDefaultGroup ? "flex" : "none"};"
          data-component="options-list"
          data-prop-name="${PROP_HANDLE_SETUP}"
          data-product-id="${h.productId}">

          <div class="grid">
            <div class="grid__item handles-setup__item handles-setup__item--radio">
              <div class="corner-radio-block">
                ${h.variants
                  .map(
                    (v, idx) => `
                      <div class="corner-radio ${POSITION_CLASS_MAP[v.pos]} ${
                      savedIdx === idx ? "active" : ""
                    }"
                        data-variant="${v.variantId}"
                        data-value="${v.pos}"
                        data-component="option-radio">
                      </div>
                    `
                  )
                  .join("")}
              </div>
            </div>

            <div class="grid__item unselectable">
              ${h.variants
                .map(
                  (v, idx) => `
                    <img src="${v.img}"
                      class="variant-image variant-image-${v.variantId}${
                    idx === shownIdx ? " selected" : ""
                  }"
                      style="display:${idx === shownIdx ? "flex" : "none"};">
                  `
                )
                .join("")}
            </div>
          </div>
        </div>
      `;
      }).join("")}
    </div>
  `;

  return wrap;
}

// ========= EVENTS =========

// ========= EDGES CART FUNCTIONS =========

function findEdgeForDoor(cart, doorItemKey) {
  return cart.items.find(
    (it) =>
      it.properties?._linked_type === "edge" && linkedKeyOf(it) === doorItemKey
  );
}

function calculateEdgeQuantity(edgePosition, width, height) {
  if (!edgePosition) return 0;

  let edgeLength = 0;
  let edgeDefQuantity = 0;

  if (edgePosition === "All Edges") {
    edgeLength += Number(height) * 2;
    edgeLength += Number(width) * 2;
    edgeDefQuantity += 40;
  } else if (edgePosition === "Floor Unit" || edgePosition === "Wall Unit") {
    edgeLength += Number(height) * 2;
    edgeLength += Number(width);
    edgeDefQuantity += 30;
  } else if (edgePosition === "Tall Unit") {
    edgeLength += Number(height) * 2;
    edgeDefQuantity += 20;
  } else if (edgePosition === "Left Side" || edgePosition === "Right Side") {
    edgeLength += Number(height);
    edgeDefQuantity += 10;
  } else if (edgePosition === "Top Side" || edgePosition === "Bottom") {
    edgeLength += Number(width);
    edgeDefQuantity += 10;
  } else if (edgePosition === "No Edges") {
    return 0;
  }

  return Math.ceil(edgeLength / 100) + edgeDefQuantity;
}

async function addEdgeToCart({
  variantId,
  doorItem,
  edgePosition,
  width,
  height,
  quantity: quantityOverride,
}) {
  const quantity =
    typeof quantityOverride === "number"
      ? quantityOverride
      : calculateEdgeQuantity(edgePosition, width, height);

  if (quantity === 0) {
    log("Edge quantity is 0, skipping add");
    return;
  }

  const doorProps = getItemProps(doorItem);
  const propertyNumber = numberOf(doorItem) || generatePropertyNumber();

  await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: quantity,
          properties: {
            [PROP_EDGES]: edgePosition,
            [PROP_FOR]: describeDoorFor(doorItem),
            [PROP_LINKED_KEY]: doorItem.key,
            [PROP_PARENT_TYPE]: doorItem.title,
            [PROP_NUMBER]: propertyNumber,
            ...(doorProps[PROP_DOOR_SEQ]
              ? { [PROP_DOOR_REF]: doorProps[PROP_DOOR_SEQ] }
              : {}),
            ...(doorProps[PROP_DOOR] ? { [PROP_DOOR]: doorProps[PROP_DOOR] } : {}),
            // Edging is priced by unit, so the line quantity isn't an
            // orderable count — 1 edging "order" per door.
            [PROP_QTY]: "1",
            _linked_type: "edge",
            ...(getItemProps(doorItem)[PROP_BUILDER]
              ? { [PROP_BUILDER]: getItemProps(doorItem)[PROP_BUILDER] }
              : {})
          },
        },
      ],
    }),
  });

  await refreshCartDrawer();
}

function generatePropertyNumber() {
  return `${Date.now().toString().slice(-4)}-${Math.floor(Math.random() * 1000)}`;
}

// ========= EDGES INTERACTIONS =========

// Resolve the door's form inputs that drive edge pricing/quantity.
function getEdgeContext(item) {
  const form = document
    .querySelector(`form .product-variant-id[value="${item.variant_id}"]`)
    ?.closest("form");
  if (!form) {
    console.error("Form not found for variant", item.variant_id);
    return null;
  }

  const width = form.querySelector('input[name="properties[Width]"]')?.value;
  const height = form.querySelector('input[name="properties[Height]"]')?.value;
  const edgeVariantId = form.querySelector(
    'input[name="edge_variant_id"]',
  )?.value;

  if (!width || !height || !edgeVariantId) {
    console.error("Required edge inputs not found", {
      width,
      height,
      edgeVariantId,
    });
    return null;
  }

  return { form, width, height, edgeVariantId };
}

function clearCustomEdges(edgesSetup) {
  const customCard = edgesSetup.querySelector(".edge_type--custom");
  if (!customCard) return;
  customCard.classList.remove("active");
  customCard
    .querySelectorAll(".edge_type_option")
    .forEach((el) => el.classList.remove("active"));
  const totalNode = customCard.querySelector(".custom-edge-total");
  if (totalNode) {
    totalNode.textContent = "£0.00";
    totalNode.style.opacity = "1";
  }
}

// Recompute the running total for the Custom card from its selected edges.
async function refreshCustomEdgeTotal(customCard, item) {
  if (!customCard) return;
  const totalNode = customCard.querySelector(".custom-edge-total");
  if (!totalNode) return;

  const selected = Array.from(
    customCard.querySelectorAll(".circle.active"),
  ).map((el) => el.dataset.value);

  if (!selected.length) {
    totalNode.textContent = "£0.00";
    totalNode.style.opacity = "1";
    return;
  }

  const ctx = getEdgeContext(item);
  if (!ctx) return;

  const unitPrice = await getVariantPrice(ctx.edgeVariantId);
  if (typeof unitPrice !== "number") return;

  const totalQuantity = selected.reduce(
    (sum, pos) => sum + calculateEdgeQuantity(pos, ctx.width, ctx.height),
    0,
  );
  totalNode.textContent = `£${(unitPrice * totalQuantity).toFixed(2)}`;
  // Custom price stays visible at all times (shows £0.00 when nothing is
  // selected, like the "No Edges" card).
  totalNode.style.opacity = "1";
}

// Preset cards (All / Floor / Wall / Tall / No Edges) — single select.
async function handlePresetEdgeClick(option, item) {
  const edgesSetup = option.closest(".edges_setup");
  if (!edgesSetup) return;

  // Cancel any pending custom-edge commit so it can't fire after this preset's
  // own cart write and race it.
  const pendingCustom = customEdgeCommitTimers.get(item.key);
  if (pendingCustom) {
    clearTimeout(pendingCustom);
    customEdgeCommitTimers.delete(item.key);
  }

  const edgePosition = option.dataset.value;
  const ctx = getEdgeContext(item);
  if (!ctx) return;

  const totalEdgeQuantity = calculateEdgeQuantity(
    edgePosition,
    ctx.width,
    ctx.height,
  );
  const unitPrice = await getEdgeUnitPrice(option, ctx.edgeVariantId);
  const totalEdgePrice =
    typeof unitPrice === "number" ? unitPrice * totalEdgeQuantity : null;

  const priceNode = ensureEdgeTotalPriceNode(option);
  if (priceNode) {
    priceNode.textContent =
      typeof totalEdgePrice === "number" ? `£${totalEdgePrice.toFixed(2)}` : "";
  }

  // Single-select: clear every other card (including the Custom selection).
  edgesSetup
    .querySelectorAll(".edge_type, .edge_type_option")
    .forEach((el) => el.classList.remove("active"));
  clearCustomEdges(edgesSetup);
  option.classList.add("active");

  /* CART LOGIC */
  try {
    const cart = await fetchCart();
    const existingEdge = findEdgeForDoor(cart, item.key);
    if (existingEdge) {
      await removeCartItem(existingEdge.key);
    }
    if (edgePosition === "No Edges") {
      return;
    }
    await addEdgeToCart({
      variantId: ctx.edgeVariantId,
      doorItem: item,
      edgePosition,
      width: ctx.width,
      height: ctx.height,
    });
  } catch (err) {
    console.error("[EDGES] cart update failed", err);
  }
}

// Pending "commit custom edges to cart" timers, keyed by door line-item key.
// Rapid clicking would otherwise fire overlapping add/remove cart calls that
// race each other (the cause of the 400 Bad Request on /cart/change.js), so we
// debounce the cart write and only run it once the shopper stops clicking.
const CUSTOM_EDGE_COMMIT_DELAY = 2000;
const customEdgeCommitTimers = new Map();

// Custom card — each edge toggles independently (free selection).
// UI (price + active state) updates immediately; the cart write is debounced.
async function handleCustomEdgeClick(circle, item) {
  const edgesSetup = circle.closest(".edges_setup");
  const customCard = circle.closest(".edge_type--custom");
  if (!edgesSetup || !customCard) return;

  // --- Immediate UI update ---
  // Toggle this edge; switching to Custom clears any active preset selection
  // (the preset *prices* stay on their cards — only the active state changes).
  circle.classList.toggle("active");
  edgesSetup
    .querySelectorAll(".edge_type:not(.edge_type--custom)")
    .forEach((el) => el.classList.remove("active"));

  const selected = Array.from(
    customCard.querySelectorAll(".circle.active"),
  ).map((el) => el.dataset.value);

  customCard.classList.toggle("active", selected.length > 0);

  // Show the updated custom price right away.
  await refreshCustomEdgeTotal(customCard, item);

  // --- Debounced cart write ---
  // Restart the 2s timer on every click; the cart only updates once the shopper
  // has stopped toggling custom edges for CUSTOM_EDGE_COMMIT_DELAY.
  scheduleCustomEdgeCartUpdate(item, customCard);
}

// (Re)start the debounce timer that commits the current custom selection to the
// cart. Re-reads the selection at fire time so it always reflects the final state.
function scheduleCustomEdgeCartUpdate(item, customCard) {
  const pending = customEdgeCommitTimers.get(item.key);
  if (pending) clearTimeout(pending);

  const timer = setTimeout(() => {
    customEdgeCommitTimers.delete(item.key);
    commitCustomEdgesToCart(item, customCard).catch((err) =>
      console.error("[EDGES] custom cart update failed", err),
    );
  }, CUSTOM_EDGE_COMMIT_DELAY);

  customEdgeCommitTimers.set(item.key, timer);
}

// Replace the door's edge line with one matching the currently selected custom
// edges. Runs as a single, non-overlapping cart write (the timer guarantees one).
async function commitCustomEdgesToCart(item, customCard) {
  const ctx = getEdgeContext(item);
  if (!ctx) return;

  const selected = Array.from(
    customCard.querySelectorAll(".circle.active"),
  ).map((el) => el.dataset.value);

  const cart = await fetchCart();
  const existingEdge = findEdgeForDoor(cart, item.key);
  if (existingEdge) {
    await removeCartItem(existingEdge.key);
  }
  if (!selected.length) {
    return;
  }

  const totalQuantity = selected.reduce(
    (sum, pos) => sum + calculateEdgeQuantity(pos, ctx.width, ctx.height),
    0,
  );
  await addEdgeToCart({
    variantId: ctx.edgeVariantId,
    doorItem: item,
    edgePosition: selected.join(", "),
    width: ctx.width,
    height: ctx.height,
    quantity: totalQuantity,
  });
}

function bindEdgesInteractions(rootEl, item) {
  if (!rootEl) return;

  rootEl
    .querySelectorAll('.edge_type[data-component="option"]')
    .forEach((option) => {
      option.addEventListener("click", () => handlePresetEdgeClick(option, item));
    });

  rootEl
    .querySelectorAll('.edge_type--custom .circle[data-component="option-radio"]')
    .forEach((circle) => {
      circle.addEventListener("click", () => handleCustomEdgeClick(circle, item));
    });
}

function findHandleForDoor(cart, doorItemKey) {
  return cart.items.find(
    (it) =>
      it.properties?._linked_type === "handle" &&
      linkedKeyOf(it) === doorItemKey
  );
}

async function removeCartItem(lineKey) {
  log("Removing handle", lineKey);

  await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: lineKey,
      quantity: 0,
    }),
  });

  await refreshCartDrawer();
}

async function addHandleToCart({ variantId, doorItem, handlePosition }) {
  const doorProps = getItemProps(doorItem);
  const propertyNumber = numberOf(doorItem) || generatePropertyNumber();
  await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {
            // Stored on the handle line so the diagram can be restored when the
            // step is re-opened or rebuilt.
            [PROP_HANDLE_SETUP]: handlePosition,
            [PROP_FOR]: describeDoorFor(doorItem),
            [PROP_LINKED_KEY]: doorItem.key,
            [PROP_PARENT_TYPE]: doorItem.title,
            [PROP_NUMBER]: propertyNumber,
            ...(doorProps[PROP_DOOR_SEQ]
              ? { [PROP_DOOR_REF]: doorProps[PROP_DOOR_SEQ] }
              : {}),
            ...(doorProps[PROP_DOOR] ? { [PROP_DOOR]: doorProps[PROP_DOOR] } : {}),
            _linked_type: "handle",
            ...(getItemProps(doorItem)[PROP_BUILDER]
              ? { [PROP_BUILDER]: getItemProps(doorItem)[PROP_BUILDER] }
              : {})
          },
        },
      ],
    }),
  });

  await refreshCartDrawer();
}

// Reset a handle diagram to its empty (no-selection) state. Crucially this
// keeps the DEFAULT variant image visible — hiding every image collapses the
// image cell, which drops the diagram's height and lets the absolutely
// positioned corner boxes overflow onto the content below it.
function resetHandlesDiagram(rootEl) {
  if (!rootEl) return;
  rootEl
    .querySelectorAll(".corner-radio.active")
    .forEach((el) => el.classList.remove("active"));
  rootEl.querySelectorAll(".handles-setup").forEach((setup) => {
    setup.querySelectorAll(".variant-image").forEach((img, i) => {
      img.classList.toggle("selected", i === 0);
      img.style.display = i === 0 ? "flex" : "none";
    });
  });
}

// Remove this door's handle line from the cart, if it has one.
async function removeHandleForDoor(doorItemKey) {
  const cart = await fetchCart();
  const existingHandle = findHandleForDoor(cart, doorItemKey);
  if (existingHandle) await removeCartItem(existingHandle.key);
}

function bindHandlesInteractions(rootEl, item) {
  if (!rootEl) return;

  // // Explicit "No handle for this door": ticking it clears any handle already in
  // // the cart and locks the picker, so a plain door is a recorded choice rather
  // // than a step the shopper appears to have skipped.
  // const noHandleInput = rootEl.querySelector('[data-component="no-handle"]');
  // if (noHandleInput) {
  //   noHandleInput.addEventListener("change", async () => {
  //     const enabled = noHandleInput.checked;
  //     rootEl.classList.toggle("is-no-handle", enabled);
  //     setNoHandleDoor(item.key, enabled);
  //     if (!enabled) return;

  //     resetHandlesDiagram(rootEl);
  //     try {
  //       await removeHandleForDoor(item.key);
  //     } catch (err) {
  //       console.error("[HANDLES] no-handle clear failed", err);
  //     }
  //   });
  // }

  // // Choosing a position is the opposite of "no handle", so it releases the lock.
  // function clearNoHandleFlag() {
  //   if (!isNoHandleDoor(item.key)) return;
  //   setNoHandleDoor(item.key, false);
  //   rootEl.classList.remove("is-no-handle");
  //   if (noHandleInput) noHandleInput.checked = false;
  // }

  // Choosing a handle size or position is the opposite of "no handle", so it
  // releases the lock.
  function clearNoHandleFlag() {
    if (!isNoHandleDoor(item.key)) return;
    setNoHandleDoor(item.key, false);
    rootEl.classList.remove("is-no-handle");
  }

  // Per-door "Clear handle selection": remove this door's handle from the cart
  // and reset the diagram so the shopper can change their mind at any time.
  rootEl
    .querySelectorAll('[data-component="clear-handle"]')
    .forEach((clearBtn) => {
      clearBtn.addEventListener("click", async () => {
        resetHandlesDiagram(rootEl);
        try {
          const cart = await fetchCart();
          const existingHandle = findHandleForDoor(cart, item.key);
          if (existingHandle) await removeCartItem(existingHandle.key);
        } catch (err) {
          console.error("[HANDLES] clear failed", err);
        }
      });
    });

  const radios = rootEl.querySelectorAll(
    '.corner-radio[data-component="option-radio"]'
  );

  radios.forEach((radio) => {
    radio.addEventListener("click", async () => {
      const setupEl = radio.closest(".handles-setup");
      if (!setupEl) return;

      clearNoHandleFlag();

      const variantId = radio.dataset.variant;
      const handlePosition = radio.dataset.value;

      /* UI state */
      setupEl
        .querySelectorAll(".corner-radio")
        .forEach((el) => el.classList.remove("active"));

      radio.classList.add("active");

      setupEl.querySelectorAll(".variant-image").forEach((img) => {
        const isActive = img.classList.contains(`variant-image-${variantId}`);
        img.classList.toggle("selected", isActive);
        img.style.display = isActive ? "flex" : "none";
      });

      /* CART LOGIC */
      try {
        const cart = await fetchCart();

        const existingHandle = findHandleForDoor(cart, item.key);

        if (existingHandle) {
          log("Existing handle found → replacing", existingHandle.key);
          await removeCartItem(existingHandle.key);
        }

        await addHandleToCart({
          variantId,
          doorItem: item,
          handlePosition,
        });

      } catch (err) {
        console.error("[HANDLES] cart update failed", err);
      }
    });
  });

  const selectEl = rootEl.querySelector('select[data-id="Handles"]');
  if (selectEl) {
    selectEl.addEventListener("change", async (e) => {
      // "No handle for this door": record the choice, hide the position picker
      // and drop any handle already in the cart for this door.
      if (e.target.value === NO_HANDLE_VALUE) {
        rootEl.classList.add("is-no-handle");
        rootEl.classList.remove("is-no-selection");
        setNoHandleDoor(item.key, true);
        resetHandlesDiagram(rootEl);
        try {
          await removeHandleForDoor(item.key);
        } catch (err) {
          console.error("[HANDLES] no-handle clear failed", err);
        }
        return;
      }

      const selectedProductId = e.target.selectedOptions[0]?.dataset.productId;
      if (!selectedProductId) return;

      // A real handle size was picked: release the "no handle" lock and show
      // the position picker.
      clearNoHandleFlag();
      rootEl.classList.remove("is-no-selection");

      const currentActive = rootEl.querySelector(".handles-setup.active");
      const currentSelectedRadio = currentActive?.querySelector(
        ".corner-radio.active"
      );
      const selectedDataValue = currentSelectedRadio?.dataset.value;

      rootEl.querySelectorAll(".handles-setup").forEach((setup) => {
        setup.classList.remove("active");
        setup.style.display = "none";
      });

      const targetSetup = rootEl.querySelector(
        `.handles-setup[data-product-id="${selectedProductId}"]`
      );

      if (targetSetup) {
        targetSetup.classList.add("active");
        targetSetup.style.display = "flex";

        if (selectedDataValue) {
          const targetRadio = targetSetup.querySelector(
            `.corner-radio[data-value="${selectedDataValue}"]`
          );
          if (targetRadio) {
            log("Auto-clicking matching corner-radio", selectedDataValue);
            targetRadio.click();
          }
        }
      }
    });
  }
}

function getEndingsContainer() {
  return document.querySelector(".endings_setup");
}

function getHandlesContainer() {
  return document.querySelector(".handles_setup");
}

// ========= MAIN =========
async function renderIkeaDoorOptions() {

  const cart = await fetchCart();
  if (!cart || !cart.items) {
    return;
  }

  // Only show doors that belong to THIS configurator. A single cart can hold
  // both kitchen and wardrobe doors (they share a checkout), but each builder
  // page must render only its own doors in the edge/handle steps.
  const builderType = getBuilderType();
  // The visible Door ordinal is global to the cart, matching cart drawer labels.
  // _DoorSeq is the stable identity/order key; _Number remains a group key only.
  const allDoorItemsInAddOrder = sortDoorsByAddOrder(
    cart.items.filter((it) => Boolean(getItemProps(it)[PROP_DOOR_SEQ]) && !isLinkedAddon(it)),
  );
  const globalDoorNumbers = new Map(
    allDoorItemsInAddOrder.map((it, index) => [it.key, String(index + 1)]),
  );
  const ikeaDoorItems = sortDoorsByAddOrder(
    cart.items.filter((it) => {
      if (!isIkeaDoorItem(it)) return false;
      if (!builderType) return true; // No builder context → preserve legacy behaviour.
      return normalize(getItemProps(it)[PROP_BUILDER]) === normalize(builderType);
    }),
  );
  if (!ikeaDoorItems.length) {
    // Every door was removed: drop the blocks we rendered for them, otherwise
    // the step keeps showing edges/handles for doors that are no longer in the
    // cart until the page is reloaded.
    renderedDoorKeys = [];
    renderedAtLeastOnce = true;
    pruneNoHandleDoors([]);
    document
      .querySelectorAll(".ikea-edges-render, .ikea-handles-render")
      .forEach((n) => n.remove());
    return;
  }

  renderedDoorKeys = ikeaDoorItems.map((it) => it.key);
  renderedAtLeastOnce = true;
  pruneNoHandleDoors(renderedDoorKeys);

  const endingsContainer = getEndingsContainer();
  const handlesContainer = getHandlesContainer();

  if (!endingsContainer || !handlesContainer) {
    return;
  }

  endingsContainer
    .querySelectorAll(".ikea-edges-render")
    .forEach((n) => n.remove());

  handlesContainer
    .querySelectorAll(".ikea-handles-render")
    .forEach((n) => n.remove());

  ikeaDoorItems.forEach((cartItem, ind) => {
    // Display numbering is derived from the current filtered cart, never from
    // the historical Door property. Keep the original line key for add-on links.
    const currentDoorNumber = globalDoorNumbers.get(cartItem.key) || String(ind + 1);
    const item = {
      ...cartItem,
      properties: {
        ...getItemProps(cartItem),
        [PROP_DOOR]: currentDoorNumber,
      },
    };

    // Mirror the door's saved edge selection (stored on the linked edge line
    // item) onto the props so the cards restore their active state on reload.
    const props = { ...getItemProps(item) };
    const existingEdge = findEdgeForDoor(cart, item.key);
    const savedEdge = existingEdge && getItemProps(existingEdge)[PROP_EDGES];
    if (savedEdge) {
      props[PROP_EDGES] = savedEdge;
    }

    // Same idea for handles: the selection lives on the linked handle line, so
    // mirror it back onto the props the diagram is built from. Lines added
    // before handle positions were recorded are resolved from the variant id.
    const existingHandle = findHandleForDoor(cart, item.key);
    if (existingHandle) {
      const handleIndex = HANDLES_CONFIG.findIndex((config) =>
        config.variants.some(
          (v) => String(v.variantId) === String(existingHandle.variant_id),
        ),
      );
      if (handleIndex > -1) props._handleIndex = handleIndex;

      const savedPosition =
        getItemProps(existingHandle)[PROP_HANDLE_SETUP] ||
        HANDLES_CONFIG[handleIndex]?.variants.find(
          (v) => String(v.variantId) === String(existingHandle.variant_id),
        )?.pos;
      if (savedPosition) props[PROP_HANDLE_SETUP] = savedPosition;
    }

    /* -------- EDGES -------- */
    const edgesEl = buildEdgesBlock({
      title: item.title,
      ind,
      doorNumber: currentDoorNumber,
      key: item.key,
      variantId: item.variant_id,
      props,
      optionName: PROP_EDGES,
    });

    edgesEl.classList.add("ikea-edges-render");
    endingsContainer.appendChild(edgesEl);
    initEdgeTotalPriceNodes(edgesEl);
    bindEdgesInteractions(edgesEl, item);
    // Compute every preset edge price up-front so the shopper sees the cost as
    // soon as the edges open, not only after clicking a card.
    renderEdgePricesForBlock(edgesEl).catch(() => {});
    refreshCustomEdgeTotal(
      edgesEl.querySelector(".edge_type--custom"),
      item,
    ).catch(() => {});

    /* -------- HANDLES -------- */
    const handlesEl = buildHandlesBlock({
      title: item.title,
      ind,
      doorNumber: currentDoorNumber,
      key: item.key,
      variantId: item.variant_id,
      props,
    });

    handlesEl.classList.add("ikea-handles-render");
    handlesContainer.appendChild(handlesEl);

    bindHandlesInteractions(handlesEl, item);

  });
}

document.addEventListener("click", (e) => {
  if (!e.target.closest(".js-ikea-dynamic-button")) return;
  renderIkeaDoorOptions().catch(console.error);
});

// ========= KEEPING THE STEP IN STEP WITH THE CART =========
// The edge/handle step is built from the cart, but the cart keeps moving after
// it is built: the shopper can go back up to the grid, add another door, remove
// one, or change quantities. Everything below exists so the step reflects the
// cart at all times without a manual page refresh.
let renderedDoorKeys = [];
let setupRefreshTimer = null;
let renderedAtLeastOnce = false;

async function currentDoorKeys() {
  const cart = await fetchCart();
  const builderType = getBuilderType();
  // Same ordering as renderIkeaDoorOptions, so the comparison against
  // renderedDoorKeys reports a real add/remove and not a reshuffle.
  return sortDoorsByAddOrder(
    (cart?.items || []).filter((it) => {
      if (!isIkeaDoorItem(it)) return false;
      if (!builderType) return true;
      return (
        normalize(getItemProps(it)[PROP_BUILDER]) === normalize(builderType)
      );
    }),
  ).map((it) => it.key);
}

function sameDoorKeys(a, b) {
  return a.length === b.length && a.every((key, i) => key === b[i]);
}

// Deliberately NOT gated on the panel being open. The step used to skip the
// refresh while it was closed, which is exactly the case the shopper hits:
// open the step, go back, add a door, return — and the door is missing because
// the change was dropped while the panel was hidden. Rebuilding a hidden panel
// costs nothing and leaves it correct whenever it is shown again.
function scheduleSetupRefresh() {
  clearTimeout(setupRefreshTimer);
  setupRefreshTimer = setTimeout(() => {
    currentDoorKeys()
      .then((keys) => {
        // Rebuild only when doors were added or removed. Our own edge/handle
        // writes also churn the cart drawer, and re-rendering on those would
        // wipe a selection the shopper is still making.
        if (renderedAtLeastOnce && sameDoorKeys(keys, renderedDoorKeys)) return;
        return renderIkeaDoorOptions();
      })
      .catch((err) => console.error("[IKEA-SETUP] refresh failed", err));
  }, 350);
}

// The builder writes to the cart with bare fetch() calls that never go through
// the theme's cart pub/sub, so the only reliable signal that the cart changed
// is the request itself. Patch once, re-broadcast as a DOM event.
const CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;
const CART_CHANGED_EVENT = "builder:cart-changed";

function emitCartChanged() {
  document.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT));
}

function watchCartWrites() {
  if (window.__builderCartWriteWatch) return;
  window.__builderCartWriteWatch = true;

  const originalFetch = window.fetch;
  if (typeof originalFetch === "function") {
    window.fetch = function (...args) {
      const input = args[0];
      const url =
        typeof input === "string"
          ? input
          : input && typeof input.url === "string"
            ? input.url
            : "";
      const isCartWrite = CART_WRITE_RE.test(url);
      return originalFetch.apply(window, args).then((response) => {
        if (isCartWrite && response && response.ok) emitCartChanged();
        return response;
      });
    };
  }

  const originalSend = window.XMLHttpRequest?.prototype?.send;
  const originalOpen = window.XMLHttpRequest?.prototype?.open;
  if (originalSend && originalOpen) {
    window.XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__builderCartWrite = CART_WRITE_RE.test(String(url || ""));
      return originalOpen.call(this, method, url, ...rest);
    };
    window.XMLHttpRequest.prototype.send = function (...args) {
      if (this.__builderCartWrite) {
        this.addEventListener("load", () => {
          if (this.status >= 200 && this.status < 300) emitCartChanged();
        });
      }
      return originalSend.apply(this, args);
    };
  }
}

// Re-check whenever the step is revealed, however it was revealed — the
// "Configure Edging & Handles" button is only one of the ways it opens.
function watchSetupPanelVisibility() {
  const wrappers = document.querySelectorAll(".ikea-doors-setup__wrapper");
  if (!wrappers.length) return;

  const observer = new MutationObserver((records) => {
    const opened = records.some(
      (record) =>
        record.target instanceof Element &&
        record.target.classList.contains("active"),
    );
    if (opened) scheduleSetupRefresh();
  });

  wrappers.forEach((wrapper) =>
    observer.observe(wrapper, {
      attributes: true,
      attributeFilter: ["class"],
    }),
  );
}

function watchCartForDoorChanges() {
  watchCartWrites();
  watchSetupPanelVisibility();

  document.addEventListener(CART_CHANGED_EVENT, scheduleSetupRefresh);

  // The theme's own cart events, for flows that do go through pub/sub.
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS === "object") {
    if (PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleSetupRefresh);
    }
  }

  const target = document.querySelector("cart-drawer") || document.body;
  new MutationObserver(scheduleSetupRefresh).observe(target, {
    childList: true,
    subtree: true,
  });

  // Belt and braces for flows where neither the drawer nor a cart request is
  // observed.
  document.addEventListener("click", (e) => {
    if (
      !e.target.closest(
        ".js-ikea-add-button, .quick-add__submit, .door-builder-add-to-bag, .door-builder-add-to-another",
      )
    ) {
      return;
    }
    setTimeout(scheduleSetupRefresh, 800);
  });

  // Returning to the page from bfcache (browser Back) restores a DOM built
  // against an older cart.
  window.addEventListener("pageshow", (e) => {
    if (e.persisted) scheduleSetupRefresh();
  });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) scheduleSetupRefresh();
  });
}

// The main "No" toggle on the IKEA handles step must remain a reliable way to
// remove handles: selecting "No" strips every handle from this builder's doors,
// so the shopper can opt out after having chosen some.
document.addEventListener("change", async (e) => {
  const radio = e.target;
  if (!(radio instanceof Element)) return;
  if (
    !radio.matches('.set_hadles .radio_group input[type="radio"]') ||
    !radio.closest("ikea-handles-setup")
  ) {
    return;
  }
  if (radio.value !== "no" || !radio.checked) return;

  try {
    const cart = await fetchCart();
    if (!cart || !cart.items) return;
    const builderType = getBuilderType();
    const doorKeys = cart.items
      .filter((it) => {
        if (!isIkeaDoorItem(it)) return false;
        if (!builderType) return true;
        return normalize(getItemProps(it)[PROP_BUILDER]) === normalize(builderType);
      })
      .map((it) => it.key);

    for (const key of doorKeys) {
      const handle = findHandleForDoor(cart, key);
      if (handle) await removeCartItem(handle.key);
    }

    // Reset every rendered handle diagram back to its empty state.
    document
      .querySelectorAll(".ikea-handles-render")
      .forEach((block) => resetHandlesDiagram(block));
  } catch (err) {
    console.error("[HANDLES] bulk clear via 'No' failed", err);
  }
});

// Pre-fill every edge option's price when the shopper enables edges ("Yes").
document.addEventListener("change", (e) => {
  const radio = e.target;
  if (!(radio instanceof Element)) return;
  if (
    !radio.matches('.set_endings .radio_group input[type="radio"]') ||
    !radio.closest("ikea-product-endings")
  ) {
    return;
  }
  if (radio.value === "yes" && radio.checked) {
    renderAllEdgePrices().catch(() => {});
  }
});


async function refreshCartDrawer() {
  const drawer = document.querySelector("cart-drawer");
  if (drawer && typeof drawer.onCartUpdate === "function") {
    drawer.onCartUpdate();
    drawer.classList.remove("is-empty");
    const innerEmpty = drawer.querySelector(".drawer__inner-empty");
    if (innerEmpty) innerEmpty.remove();
    drawer.querySelector("cart-drawer-items").classList.remove("is-empty");
    const counter = document.querySelector(
      "#cart-icon-bubble .cart-count-bubble"
    );
    const totalPrice = document.querySelector(
      "#CartDrawer .totals__total-value .number"
    );
    const response = await fetch("/cart.js");
    const data = await response.json();
    const innerText = `
					<span aria-hidden="true">${data.items.length}</span>
					<span class="visually-hidden">${data.items.length} item</span>
					`;

    if (totalPrice) {
      totalPrice.innerHTML = (data.items_subtotal_price / 100).toFixed(2);
    }
    if (counter) {
      counter.innerHTML = innerText;
    } else {
      const bubble = document.createElement("div");
      bubble.className = "cart-count-bubble";
      bubble.innerHTML = innerText;
      document.querySelector("#cart-icon-bubble").appendChild(bubble);
    }
    animateCartUpdate();
  }
}

const EDGE_PRICE_CACHE = new Map();

async function getVariantPrice(variantId) {
  if (!variantId) return null;
  if (EDGE_PRICE_CACHE.has(variantId)) return EDGE_PRICE_CACHE.get(variantId);

  try {
    const res = await fetch(`/variants/${variantId}.js`, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`variants/${variantId}.js failed: ${res.status}`);
    const data = await res.json();
    const price = typeof data?.price === "number" ? data.price / 100 : null;
    EDGE_PRICE_CACHE.set(variantId, price);
    return price;
  } catch (e) {
    console.warn("[EDGE-PRICE] Failed to load variant price", { variantId, e });
    EDGE_PRICE_CACHE.set(variantId, null);
    return null;
  }
}

function ensureEdgeTotalPriceNode(optionEl) {
  const nameEl = optionEl.querySelector(".name");
  if (!nameEl) return null;
  const existing = nameEl.parentElement?.querySelector(
    ":scope > .edge-total-price",
  );
  if (existing) return existing;

  const node = document.createElement("div");
  node.className = "edge-total-price";
  node.textContent = "";
  nameEl.insertAdjacentElement("afterend", node);
  return node;
}

function initEdgeTotalPriceNodes(edgesRootEl) {
  if (!edgesRootEl) return;

  const edgeOptions = edgesRootEl.querySelectorAll(
    '.edge_type[data-component="option"], .edge_type_option[data-component="option-radio"]',
  );

  edgeOptions.forEach((optionEl) => {
    ensureEdgeTotalPriceNode(optionEl);
  });
}

async function renderEdgePricesForBlock(edgesBlockEl) {
  if (!edgesBlockEl) return;

  const doorVariantId = edgesBlockEl.getAttribute("data-variant-id");
  const form = document
    .querySelector(`form .product-variant-id[value="${doorVariantId}"]`)
    ?.closest("form");
  if (!form) return;

  const width = form.querySelector('input[name="properties[Width]"]')?.value;
  const height = form.querySelector('input[name="properties[Height]"]')?.value;
  const edgeVariantId = form.querySelector(
    'input[name="edge_variant_id"]',
  )?.value;
  if (!width || !height || !edgeVariantId) return;

  const unitPrice = await getVariantPrice(edgeVariantId);
  if (typeof unitPrice !== "number") return;

  const options = edgesBlockEl.querySelectorAll(
    '.edge_type[data-component="option"], .edge_type_option[data-component="option-radio"]',
  );

  options.forEach((optionEl) => {
    const priceNode = ensureEdgeTotalPriceNode(optionEl);
    if (!priceNode) return;

    const value = optionEl.dataset.value;
    // "No Edges" is always free — keep its price visible and pinned to £0.00.
    if (value === "No Edges") {
      priceNode.textContent = "£0.00";
      return;
    }

    const quantity = calculateEdgeQuantity(value, width, height);
    priceNode.textContent =
      quantity > 0 ? `£${(unitPrice * quantity).toFixed(2)}` : "";
  });
}

function renderAllEdgePrices() {
  const blocks = document.querySelectorAll(".ikea-edges-render");
  return Promise.all(
    Array.from(blocks).map((block) =>
      renderEdgePricesForBlock(block).catch(() => {}),
    ),
  );
}

async function getEdgeUnitPrice(clickedEl, edgeVariantId) {
  const endingTypeSelected = clickedEl.closest(".ending-type.selected");
  if (endingTypeSelected?.dataset?.optionPrice) {
    const v = Number(endingTypeSelected.dataset.optionPrice);
    if (!Number.isNaN(v)) return v / 100;
  }
  const endingInfo = clickedEl.closest(".ending_info");
  if (endingInfo?.dataset?.edgePrice) {
    const v = Number(endingInfo.dataset.edgePrice);
    if (!Number.isNaN(v)) return v / 100;
  }

  const vPrice = await getVariantPrice(edgeVariantId);
  return vPrice;
}

// ========= BASKET DOOR NUMBERING =========
// The cart drawer is server-rendered and re-rendered via AJAX on every cart
// change, so we re-apply numbering from a MutationObserver. Each row is rebuilt
// from its clean `data-product-title`, which keeps this idempotent.
function rowIsLinkedAddon(propsStr) {
  return /(^|;\s*)(_LinkedTo|_ParentProduct|Linked To|Parent Product|_linked_type)\s*:/i.test(
    propsStr || "",
  );
}

// Parses the `Door: N;` entry out of a cart row's `data-properties` string
// (format: "key: value; key: value; ", see snippets/cart-drawer.liquid).
// Returns the trimmed value, or null if there is no Door property.
function parseDoorNumberFromPropsString(propsStr) {
  if (!propsStr) return null;
  const match = /(?:^|;)\s*Door\s*:\s*([^;]*)/i.exec(propsStr);
  if (!match) return null;
  const value = match[1].trim();
  return value || null;
}

function renderBasketDoorNumber(nameEl, title, doorNumber) {
  const numSpan = document.createElement("span");
  numSpan.className = "door-number";
  numSpan.textContent = `Door ${doorNumber}`;
  nameEl.replaceChildren(numSpan, document.createTextNode(` — ${title}`));
}

function parseDoorSequenceFromPropsString(propsStr) {
  const match = /(?:^|;)\s*_DoorSeq\s*:\s*([^;]*)/i.exec(propsStr || "");
  const value = match ? Number(match[1].trim()) : NaN;
  return Number.isFinite(value) ? value : null;
}

function applyBasketDoorNumbers(drawer) {
  if (!drawer) return;

  // Shopify often prepends new cart lines. Sort by the stable add sequence,
  // then assign fresh display numbers; never trust the stale visible Door prop.
  const rows = Array.from(drawer.querySelectorAll("tr.cart-item"))
    .filter((row) => {
      const title = row.dataset.productTitle || "";
      const props = row.dataset.properties || "";
      const hasDoorSequence = /(?:^|;)\s*_DoorSeq\s*:/i.test(props);
      return (hasDoorSequence || containsIkeaDoor(title)) && !rowIsLinkedAddon(props);
    })
    .map((row, domIndex) => ({
      row,
      domIndex,
      sequence: parseDoorSequenceFromPropsString(row.dataset.properties),
    }))
    .sort((a, b) => {
      if (a.sequence != null && b.sequence != null) return a.sequence - b.sequence;
      if (a.sequence != null) return 1;
      if (b.sequence != null) return -1;
      // Legacy lines have no sequence; reverse DOM order to counter Shopify's
      // newest-first cart ordering.
      return b.domIndex - a.domIndex;
    });

  rows.forEach(({ row }, index) => {
    const title = row.dataset.productTitle || "";
    const nameEl = row.querySelector(".cart-item__name");
    if (!nameEl) return;
    const number = String(index + 1);
    const builderLabel = row.querySelector(".cart-item__builder-ref");
    if (builderLabel) {
      // Liquid has already rendered the row's label. Update just its number so
      // the product title is not duplicated by a second "Door N" prefix.
      builderLabel.textContent = builderLabel.textContent.replace(/Door\s+\d+/, `Door ${number}`);
      return;
    }
    // Compatibility fallback for legacy IKEA lines the Liquid snippet cannot
    // resolve (e.g. lines without _DoorSeq).
    renderBasketDoorNumber(nameEl, title, number);
  });
}

function observeBasketDoorNumbers() {
  const drawer = document.querySelector("cart-drawer");
  if (!drawer) return;

  const observer = new MutationObserver(() => {
    // Detach while we mutate so our own writes don't retrigger the observer.
    observer.disconnect();
    applyBasketDoorNumbers(drawer);
    observer.observe(drawer, { childList: true, subtree: true });
  });

  applyBasketDoorNumbers(drawer);
  observer.observe(drawer, { childList: true, subtree: true });
}

function animateCartUpdate() {
  const targets = [
    document.querySelector("#cart-icon-bubble"),
    document.querySelector("cart-drawer .drawer__header"),
  ].filter(Boolean);

  targets.forEach((target) => {
    target.classList.remove("ikea-cart-updated");
    // Force reflow so repeated quick changes restart the short animation.
    void target.offsetWidth;
    target.classList.add("ikea-cart-updated");
    target.addEventListener("animationend", () => {
      target.classList.remove("ikea-cart-updated");
    }, { once: true });
  });
}

function installCartUpdateAnimation() {
  if (document.getElementById("ikea-cart-update-animation-style")) return;
  const style = document.createElement("style");
  style.id = "ikea-cart-update-animation-style";
  style.textContent = `
    #cart-icon-bubble.ikea-cart-updated,
    cart-drawer .drawer__header.ikea-cart-updated {
      animation: ikea-cart-update-pulse 420ms ease-out;
    }
    @keyframes ikea-cart-update-pulse {
      0% { transform: scale(1); }
      45% { transform: scale(1.045); }
      100% { transform: scale(1); }
    }
    @media (prefers-reduced-motion: reduce) {
      #cart-icon-bubble.ikea-cart-updated,
      cart-drawer .drawer__header.ikea-cart-updated {
        animation: none;
      }
    }
  `;
  document.head.appendChild(style);
}

function initIkeaSetupObservers() {
  installCartUpdateAnimation();
  document.addEventListener(CART_CHANGED_EVENT, animateCartUpdate);
  observeBasketDoorNumbers();
  watchCartForDoorChanges();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initIkeaSetupObservers);
} else {
  initIkeaSetupObservers();
}
})();
